import { exampleReportParam } from "./example-report-links.js";
import { Link } from "react-router";
import { CompassMark } from "./compass-mark.js";
import { ArrowRight, Check, CircleAlert, Layers3, ShieldCheck, Target } from "lucide-react";
import type { ReactNode } from "react";
import {
  candidateProjects,
  matchActions,
  roleCoverageFields,
  roleOutcomes,
  sampleCapabilities,
  sampleComparison,
  sampleComparisonSummary,
  sampleSameCount,
} from "./sample-report-data.js";
import { CapabilityRadar, CapabilitySignals, CoverageRing } from "./sample-report-visuals.js";
import "./sample-reports.css";

const candidateFacts = [
  ["Experience", "12 years", "Engineering + Cybersecurity"],
  ["Location", "Columbus, OH", "Hybrid · Up to 25% travel"],
  ["Target base", "$150k to $165k", "Full-time · Available in 4 weeks"],
  ["Operating reach", "3 plants", "14 production lines"],
] as const;
const jobFacts = [
  ["Work model", "Hybrid", "3 days on site · Columbus, OH"],
  ["Engagement", "Full-time", "Up to 25% domestic travel"],
  ["Base salary", "$150k to $175k", "Plus 10% target bonus"],
  ["Team", "OT security", "Reports to Director of Security"],
] as const;
const areas = (count: number) => `${count} ${count === 1 ? "area" : "areas"}`;
const matchFacts = [
  [
    "Same responsibility level",
    areas(sampleComparison.same.length),
    "Recorded at the role’s level",
  ],
  [
    "Relative strengths",
    areas(sampleComparison.broader.length),
    "Industrial systems and asset management",
  ],
  [
    "Needs follow-up",
    areas(sampleComparison.followUp.length),
    "Incident response responsibilities",
  ],
  ["Working preferences", "Details to confirm", "On-site schedule and start date"],
] as const;
const reportOpenings = {
  dossier: {
    kind: "Candidate Profile",
    title: "Alex Morgan",
    subtitle: "Senior OT Security Engineer · Manufacturing & critical infrastructure",
    status: "Open to a conversation",
    facts: candidateFacts,
    summary:
      "Alex has supported incident response exercises. The interview should clarify which response decisions Alex has been responsible for and what support was needed.",
  },
  brief: {
    kind: "Role Profile",
    title: "Senior OT Security Engineer",
    subtitle: "Northstar Manufacturing · Columbus, Ohio · Three US plants",
    status: "Example role profile",
    facts: jobFacts,
    summary:
      "Reduce exposure without disrupting production. Turn a multi-site OT security roadmap into practical, measurable engineering improvements.",
  },
  match: {
    kind: "Match Analysis",
    title: "Alex Morgan + Northstar",
    subtitle: "Alex’s experience compared with the Senior OT Security Engineer role profile",
    status: "Interview follow-up needed",
    facts: matchFacts,
    summary: sampleComparisonSummary,
  },
} as const;

type ComparisonStatus = "matched" | "strength" | "follow-up" | "neutral";
const metricStatuses: Readonly<Record<string, ComparisonStatus>> = {
  "Same responsibility level": "matched",
  "Relative strengths": "strength",
  "Needs follow-up": "follow-up",
};
const comparisonStatus = (candidate: number, requirement: number): ComparisonStatus =>
  candidate < requirement ? "follow-up" : candidate > requirement ? "strength" : "matched";

/** Excerpts reuse the header, facts and finding from their complete report. */
export function SampleReportExcerpt({
  report,
  compact = false,
  responsibility = false,
}: {
  report: keyof typeof reportOpenings;
  compact?: boolean;
  responsibility?: boolean;
}) {
  const opening = reportOpenings[report];
  const facts = compact
    ? report === "match"
      ? opening.facts.slice(2)
      : opening.facts.slice(0, 2)
    : opening.facts;
  return (
    <div className="sample-report insight-report sample-report-excerpt">
      <Hero report={report} {...opening} />
      <div className="ir-body">
        <Metrics items={facts} />
        <p className="ir-excerpt-finding">{opening.summary}</p>
        {responsibility && report === "brief" ? (
          <section className="ir-card">
            <h4>Responsibility detail</h4>
            <CapabilityRadar capabilities={sampleCapabilities} job />
          </section>
        ) : null}
      </div>
    </div>
  );
}

function Hero({
  report,
  kind,
  title,
  subtitle,
  status,
}: {
  report: keyof typeof reportOpenings;
  kind: string;
  title: string;
  subtitle: string;
  status: string;
}) {
  return (
    <header className="ir-hero" data-report={report}>
      <div className="ir-hero__orb" aria-hidden="true" />
      <div className="ir-hero__brand">
        <strong>Zylarity</strong>
        <CompassMark />
      </div>
      <div className="ir-hero__content">
        <div>
          <p className="ir-kicker">{kind}</p>
          <h3>{title}</h3>
          <p>{subtitle}</p>
        </div>
        <span className="ir-status">
          <i aria-hidden="true" />
          {status}
        </span>
      </div>
    </header>
  );
}

function Metrics({ items }: { items: readonly (readonly [string, string, string])[] }) {
  return (
    <dl className="ir-metrics">
      {items.map(([label, value, detail]) => (
        <div key={label} data-status={metricStatuses[label] ?? "neutral"}>
          <dt>{label}</dt>
          <dd>
            {value}
            <small>{detail}</small>
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Card({
  label,
  title,
  children,
  className = "",
}: {
  label: string;
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`ir-card ${className}`}>
      <span className="ir-kicker">{label}</span>
      <h4>{title}</h4>
      {children}
    </section>
  );
}

function Tags({ items }: { items: readonly string[] }) {
  return (
    <ul className="ir-tags">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function Footer({ children }: { children: ReactNode }) {
  return (
    <footer className="ir-footer">
      <ShieldCheck size={19} aria-hidden="true" />
      <p>{children}</p>
      <span>
        <span aria-hidden="true">*</span> Sample
      </span>
    </footer>
  );
}

export function CandidateSample() {
  return (
    <>
      <Hero report="dossier" {...reportOpenings.dossier} />
      <div className="ir-body">
        <Metrics items={candidateFacts} />
        <div className="ir-insight">
          <Target aria-hidden="true" />
          <p>
            <strong>Engineering depth. Practical delivery.</strong> Connects security architecture
            with the people, maintenance windows, and production constraints that make it work.
          </p>
        </div>
        <div className="ir-grid">
          <Card
            label="Capability map"
            title="The shape of Alex's experience"
            className="ir-card--map"
          >
            <CapabilityRadar capabilities={sampleCapabilities} />
          </Card>
          <Card
            label="Profile intelligence"
            title="Experience in context"
            className="ir-card--coverage"
          >
            <p className="ir-described-areas">{sampleCapabilities.length} work areas described</p>
            <p>
              From controls engineering to leading OT security delivery. Strongest in brownfield
              segmentation, asset visibility, and working alongside plant teams.
            </p>
            <Tags
              items={[
                "GICSP",
                "IEC 62443 training",
                "Rockwell ControlLogix",
                "Palo Alto",
                "Claroty",
              ]}
            />
            <p className="ir-small">
              The badge counts described work areas. It is not a skill score or verification rate.
            </p>
          </Card>
          <Card label="Capability signal" title="Strengths you can explore">
            <CapabilitySignals capabilities={sampleCapabilities} mode="candidate" />
          </Card>
          <Card label="Career context" title="Where Alex does the best work">
            <dl className="ir-facts">
              <div>
                <dt>Current role</dt>
                <dd>Senior OT Security Engineer, Northline Industrial Systems</dd>
              </div>
              <div>
                <dt>Career path</dt>
                <dd>5 years in controls engineering, followed by 7 years in OT security.</dd>
              </div>
              <div>
                <dt>Industries</dt>
                <dd>Discrete manufacturing, food processing, and industrial utilities.</dd>
              </div>
              <div>
                <dt>Team setting</dt>
                <dd>
                  Works with security architects, plant engineers, maintenance teams, and site
                  leadership.
                </dd>
              </div>
              <div>
                <dt>Next move</dt>
                <dd>
                  Own a multi-site security roadmap while staying close to engineering delivery.
                </dd>
              </div>
            </dl>
          </Card>
          <Card
            label="Evidence portfolio"
            title="Selected work and its impact"
            className="ir-card--wide"
          >
            <div className="ir-projects">
              {candidateProjects.map((project, index) => (
                <div key={project.title}>
                  <span className="ir-number">0{index + 1}</span>
                  <h5>{project.title}</h5>
                  <p>{project.context}</p>
                  <strong>{project.result}</strong>
                  <small>{project.evidence}</small>
                </div>
              ))}
            </div>
          </Card>
        </div>
        <div className="ir-callout">
          <CircleAlert size={21} aria-hidden="true" />
          <div>
            <h4>A useful next question</h4>
            <p>{reportOpenings.dossier.summary}</p>
          </div>
        </div>
      </div>
      <Footer>
        Example evidence includes a career interview, a segmentation design exercise, and a project
        walkthrough. Every rating is connected to an example or a follow-up question.
      </Footer>
    </>
  );
}

export function JobSample() {
  return (
    <>
      <Hero report="brief" {...reportOpenings.brief} />
      <div className="ir-body">
        <Metrics items={jobFacts} />
        <Card label="Responsibility detail" title="The responsibility the work requires">
          <CapabilityRadar capabilities={sampleCapabilities} job />
          <CapabilitySignals capabilities={sampleCapabilities} mode="job" />
        </Card>
        <div className="ir-insight">
          <Target aria-hidden="true" />
          <p>{reportOpenings.brief.summary}</p>
        </div>
        <div className="ir-grid">
          <Card label="Role requirements" title="Essential and preferred experience">
            <ul className="ir-requirements">
              <li>
                <strong>Essential</strong>
                <p>
                  Lead OT segmentation delivery, explain zones and conduits, and show how plant
                  stakeholders shape implementation.
                </p>
              </li>
              <li>
                <strong>Essential</strong>
                <p>
                  Work independently on incident triage and translate findings into a safe
                  operational response.
                </p>
              </li>
              <li>
                <strong>Preferred</strong>
                <p>
                  GICSP or IEC 62443 training, Claroty experience, and exposure to a multi-site
                  manufacturing program.
                </p>
              </li>
            </ul>
            <p className="ir-small">
              8+ years in industrial engineering or security, including hands-on OT security
              delivery. No government clearance required.
            </p>
          </Card>
          <Card label="Operating environment" title="Operating environment and decision authority">
            <p>
              Three brownfield manufacturing plants, 14 production lines, mixed-vendor controls, and
              tightly scheduled maintenance windows. Security changes require joint approval with
              site engineering.
            </p>
            <Tags
              items={[
                "Rockwell ControlLogix",
                "Siemens S7",
                "Palo Alto",
                "Claroty",
                "Microsoft Sentinel",
                "IEC 62443",
              ]}
            />
            <dl className="ir-facts">
              <div>
                <dt>Core team</dt>
                <dd>
                  Director of Security, two security engineers, and a plant engineering lead at each
                  site.
                </dd>
              </div>
              <div>
                <dt>Response model</dt>
                <dd>
                  Shared on-call rotation with plant engineering. Lead technical triage and
                  coordinate safe containment.
                </dd>
              </div>
              <div>
                <dt>Decision authority</dt>
                <dd>
                  Own technical designs and implementation plans; site leaders approve production
                  changes.
                </dd>
              </div>
            </dl>
          </Card>
          <Card label="Outcome plan" title="First 90 days" className="ir-card--wide">
            <div className="ir-timeline">
              {roleOutcomes.map((outcome) => (
                <div key={outcome.period}>
                  <span>{outcome.period}</span>
                  <h5>{outcome.title}</h5>
                  <p>{outcome.description}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>
        <div className="ir-process">
          <Layers3 aria-hidden="true" />
          <div>
            <strong>Interview pathway</strong>
            <p>
              Hiring manager conversation → technical design discussion → plant stakeholder meeting
              → mutual decision.
            </p>
          </div>
        </div>
        <details className="ir-organization">
          <summary>How this profile is organized</summary>
          <Card
            label="Profile intelligence"
            title="Document coverage"
            className="ir-card--coverage"
          >
            <CoverageRing
              value={100}
              label="Document sections completed"
              detail={`${roleCoverageFields.length} of ${roleCoverageFields.length} role areas defined`}
            />
            <ul className="ir-coverage-list">
              {roleCoverageFields.map((field) => (
                <li key={field}>
                  <Check size={14} aria-hidden="true" />
                  {field}
                </li>
              ))}
            </ul>
            <p className="ir-small">
              Coverage measures populated profile areas, not candidate fit.
            </p>
          </Card>
        </details>
      </div>
      <Footer>
        A fully populated fictional role profile connects the role's purpose, technical
        requirements, working conditions, and outcomes before a search begins.
      </Footer>
    </>
  );
}

export function MatchSample() {
  return (
    <>
      <Hero report="match" {...reportOpenings.match} />
      <div className="ir-body">
        <Metrics items={matchFacts} />
        <div className="ir-insight">
          <Target aria-hidden="true" />
          <p>
            <strong>Clarify Alex’s incident response responsibilities.</strong>{" "}
            {sampleComparisonSummary}
          </p>
        </div>
        <div className="ir-grid">
          <Card
            label="Candidate and role"
            title="Responsibility comparison"
            className="ir-card--map"
          >
            <CapabilityRadar capabilities={sampleCapabilities} comparison />
            <div className="ir-map-reading">
              <strong>How the responsibilities compare</strong>
              <p>
                {sampleSameCount} areas have the same responsibility level. Industrial systems and
                asset management describe broader responsibility than the role requires; incident
                response still needs follow-up.
              </p>
            </div>
          </Card>
          <Card label="What to explore next" title="Incident response responsibilities">
            <p>{matchActions[0].description}</p>
            <p>Alex’s experience: Assisted (level 2). Role expectation: Independent (level 3).</p>
            <p>
              The example describes assisted response work. The interview should clarify which
              decisions Alex has been responsible for and what support was needed.
            </p>
          </Card>
        </div>
        <div className="ir-match-highlights">
          <div data-status="matched">
            <Check aria-hidden="true" />
            <strong>Same responsibility level</strong>
            <p>
              {sampleComparison.same.length} areas:{" "}
              {sampleComparison.same.map((item) => item.shortLabel.toLowerCase()).join(", ")}.
            </p>
          </div>
          <div data-status="strength">
            <ArrowRight aria-hidden="true" />
            <strong>Relative strengths</strong>
            <p>
              Industrial systems and asset management are both level 4 against the role’s level 3.
            </p>
          </div>
          <div data-status="follow-up">
            <CircleAlert aria-hidden="true" />
            <strong>Needs follow-up</strong>
            <p>
              Incident response is Assisted (level 2) against Independent (level 3). Explore which
              decisions Alex could own.
            </p>
          </div>
        </div>
        <section className="ir-card ir-comparison">
          <span className="ir-kicker">Experience and follow-up</span>
          <h4>Prepare the interview questions</h4>
          <div
            className="ir-table-scroll"
            tabIndex={0}
            role="region"
            aria-label="Responsibility comparison"
          >
            <table role="table">
              <caption>Role expectations compared with Alex’s experience</caption>
              <thead>
                <tr>
                  <th scope="col">Work domain</th>
                  <th scope="col">Role expectation</th>
                  <th scope="col">Alex’s experience</th>
                  <th scope="col">Experience and follow-up</th>
                </tr>
              </thead>
              <tbody>
                {sampleCapabilities.map((item) => (
                  <tr
                    key={item.label}
                    role="row"
                    data-status={comparisonStatus(item.candidate, item.requirement)}
                  >
                    <th scope="row" role="rowheader">
                      {item.label}
                    </th>
                    <td role="cell" data-label="Role expectation">
                      {item.requirement}/5
                    </td>
                    <td role="cell" data-label="Alex’s experience">
                      {item.candidate}/5
                    </td>
                    <td role="cell" data-label="Experience and follow-up">
                      <span
                        className={`ir-evidence-status ${item.candidate < item.requirement ? "ir-evidence-status--gap" : ""}`}
                        data-status={comparisonStatus(item.candidate, item.requirement)}
                      >
                        {item.candidate < item.requirement
                          ? "Needs follow-up"
                          : item.candidate > item.requirement
                            ? "Relative strengths"
                            : "Same responsibility level"}
                      </span>
                      <p>{item.evidence}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <div className="ir-grid ir-grid--followup">
          <Card label="Practical fit" title="Schedule and start date still need confirmation">
            <dl className="ir-facts">
              <div>
                <dt>Location</dt>
                <dd>
                  Both are based in Columbus, Ohio. Alex prefers hybrid work. Confirm whether three
                  on-site days would work for Alex.
                </dd>
              </div>
              <div>
                <dt>Travel</dt>
                <dd>Candidate preference and role requirement both allow up to 25%.</dd>
              </div>
              <div>
                <dt>Base compensation</dt>
                <dd>Alex's $150k to $165k target sits within the $150k to $175k role range.</dd>
              </div>
              <div>
                <dt>Timing</dt>
                <dd>Alex is available in four weeks. Confirm the employer’s target start date.</dd>
              </div>
            </dl>
          </Card>
          <Card label="Interview plan" title="Questions to resolve before proceeding">
            <ol className="ir-actions">
              {matchActions.map((action) => (
                <li key={action.title}>
                  <strong>{action.title}</strong>
                  <p>{action.description}</p>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
      <Footer>
        Every comparison uses the fictional experience and role requirements shown in the candidate
        profile and role profile. The interview and confirmed working arrangements inform the hiring
        decision.
      </Footer>
    </>
  );
}

/** Compact, accessible live report content used consistently by the three interior heroes. */
export function SampleHeroCard({
  report,
  inlineFictionLabel = false,
}: {
  report: keyof typeof reportOpenings;
  inlineFictionLabel?: boolean;
}) {
  const opening = reportOpenings[report];
  return (
    <div className="sample-hero-card">
      <article className="sample-report insight-report" aria-label={`${opening.kind} preview`}>
        <Hero
          report={report}
          {...opening}
          kind={inlineFictionLabel ? `Fictional ${opening.kind}` : opening.kind}
        />
        <div className="ir-body">
          <Metrics items={opening.facts} />
          <Card
            label={report === "match" ? "Capability overlay" : "Capability map"}
            title={
              report === "brief"
                ? "The responsibility the work requires"
                : report === "match"
                  ? "Responsibility comparison"
                  : "The shape of Alex's experience"
            }
            className="sample-hero-radar"
          >
            <CapabilityRadar
              capabilities={sampleCapabilities}
              job={report === "brief"}
              comparison={report === "match"}
            />
          </Card>
        </div>
        <Link
          className="sample-hero-open zy-button zy-button--secondary"
          to={`/our-process/examples?report=${exampleReportParam(report)}#sample-report-preview`}
        >
          Explore the {opening.kind}
          <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </article>
      {!inlineFictionLabel && (
        <p className="sample-hero-fiction">Fictional example. No real candidate or client data.</p>
      )}
    </div>
  );
}
